import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import SelectDropdown, { SelectDropdownOption } from './SelectDropdown';
import styled from 'styled-components';
import { Button } from './Button';

// Filter configuration interface
export interface FilterConfig {
  id: string;
  key: string; //to send in queryparams
  label: string;
  options: SelectDropdownOption[];
  placeholder?: string;
  searchable?: boolean;
}

// Selected filters interface
export interface SelectedFilters {
  [key: string]: string;
}

interface FilterSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  filters: FilterConfig[];
  selectedFilters: SelectedFilters;
  appliedFilters: SelectedFilters; // Track currently applied filters
  onFilterChange: (filterId: string, value: string) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
}

const Overlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: ${props => (props.isOpen ? 'block' : 'none')};
  animation: fadeIn 0.3s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const SidePanel = styled.div<{ isOpen: boolean }>`
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 400px;
  max-width: 90vw;
  background-color: white;
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  transform: ${props => (props.isOpen ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform 0.3s ease-in-out;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 0.375rem;
  border: none;
  background-color: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e5e7eb;
    color: #374151;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
`;

const FilterGroup = styled.div`
  margin-bottom: 1.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;

const Footer = styled.div`
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background-color: #f9fafb;
  display: flex;
  gap: 0.75rem;
`;

const FilterSidePanel: React.FC<FilterSidePanelProps> = ({
  isOpen,
  onClose,
  filters,
  selectedFilters,
  appliedFilters,
  onFilterChange,
  onApplyFilters,
  onClearFilters,
}) => {
  // Close panel on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when panel is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleOverlayClick = () => {
    onClose();
  };

  const handleApply = () => {
    onApplyFilters();
    onClose();
  };

  const handleClear = () => {
    onClearFilters();
  };

  // Check if filters have changed from the applied state
  const hasFilterChanges = React.useMemo(() => {
    // Get all filter IDs from the config
    const allFilterIds = filters.map(f => f.id);
    
    // Check if any filter value is different from applied value
    return allFilterIds.some(filterId => {
      const selectedValue = selectedFilters[filterId] || '';
      const appliedValue = appliedFilters[filterId] || '';
      return selectedValue !== appliedValue;
    });
  }, [selectedFilters, appliedFilters, filters]);

  // Check if any filters are currently selected (non-empty)
  const hasSelectedFilters = Object.values(selectedFilters).some(value => (value !== '' && value !== 'all'));

  return (
    <>
      <Overlay isOpen={isOpen} onClick={handleOverlayClick} />
      <SidePanel isOpen={isOpen}>
        <Header>
          <Title>Filters</Title>
          <CloseButton onClick={onClose} aria-label="Close filters">
            <X size={20} />
          </CloseButton>
        </Header>

        <Content>
          {filters.map(filter => (
            <FilterGroup key={filter.id}>
              <FilterLabel>{filter.label}</FilterLabel>
              <SelectDropdown
                options={filter.options}
                value={selectedFilters[filter.id] || ''}
                onChange={value => onFilterChange(filter.id, value)}
                placeholder={filter.placeholder || 'Select...'}
                searchable={filter.searchable || false}
                variant="transparent"
              />
            </FilterGroup>
          ))}
        </Content>

        <Footer>
          <Button variant="secondary" onClick={handleClear} disabled={!hasSelectedFilters}>
            Clear All
          </Button>
          <Button variant="primary" onClick={handleApply} disabled={!hasFilterChanges}>
            Apply Filters
          </Button>
        </Footer> 
      </SidePanel>
    </>
  );
};

export default FilterSidePanel;
